import { z } from 'zod'
import { convert, parseDecimal, roundedText, unitByCode, UnitError } from './units.ts'

/** M15-C, GK-029: the nutrients a treatment brings, in kg/ha, from the dose and the label's DECLARED composition — never a guess (docs/M15.md C; the owner's
 *  default: "a product without them shows « non renseigné »"). Pure arithmetic, exact decimals (the units twin's rationals), the one place the rule is written;
 *  `fixtures/nutrients.json` proves it against generic worked examples (a public, textbook rule: kg/ha of a nutrient = rate × analysis / 100; no brand's label).
 *  - The composition is `catalog.product_version.formulation` constrained by `product.revise@2` to `{ basis: 'w/w' | 'w/v', N, P2O5, K2O, MgO, S, Fe }` as
 *    percentages (`@1` keeps the free jsonb, which counts as "non renseigné" here). 'w/w': % of the product's MASS; 'w/v': % mass per VOLUME (1 % w/v = 1 kg
 *    per 100 L).
 *  - The dose is a per-area rate (kg_ha, g_m2, …, or l_ha, ml_m2, …): brought to kg/ha or L/ha through the units twin, then to the basis' own quantity
 *    through the version's density when the basis and the dose cross (a mass basis with a dose in L/ha, a volume basis with a dose in kg/ha):
 *    `DENSITY_REQUIRED` without one, never assumed. kg/ha of each nutrient = quantity per ha × % / 100, three decimals. */
export const NUTRIENTS = ['N', 'P2O5', 'K2O', 'MgO', 'S', 'Fe'] as const
export type Nutrient = (typeof NUTRIENTS)[number]
export const FORMULATION_BASES = ['w/w', 'w/v'] as const
export type FormulationBasis = (typeof FORMULATION_BASES)[number]
const pct = z.number().min(0).max(100)
/** The declared composition (the zod twin of product.revise@2's `formulation`): the basis and the nutrients as percentages. */
export const nutrientFormulation = z.strictObject({ basis: z.enum(FORMULATION_BASES), N: pct.optional(), P2O5: pct.optional(), K2O: pct.optional(), MgO: pct.optional(), S: pct.optional(), Fe: pct.optional() })
export type NutrientFormulation = z.infer<typeof nutrientFormulation>

/** The declared composition of a version's formulation, or null when it is not one (a free-form @1 jsonb, nothing at all, no nutrient given): "non renseigné". */
export function declaredFormulation(formulation: unknown): NutrientFormulation | null {
  const r = nutrientFormulation.safeParse(formulation)
  if (!r.success) return null
  return NUTRIENTS.some((n) => r.data[n] !== undefined) ? r.data : null
}

export type NutrientsResult = { ok: true; kgPerHa: Partial<Record<Nutrient, string>> } | { ok: false; code: 'NOT_DECLARED' | 'DENSITY_REQUIRED' | 'UNIT_INVALID' | UnitError['code'] }
/** kg/ha of each declared nutrient for a dose (`doseValue` in `doseUnitCode`, a per-area code of ref.unit) of a product whose version declares `formulation`
 *  (with `densityKgPerL` when the basis and the dose cross). Exact: the result's text carries three decimals. */
export function nutrientsPerHectare(doseValue: string | number, doseUnitCode: string, formulation: unknown, densityKgPerL: string | number | null): NutrientsResult {
  const f = declaredFormulation(formulation)
  if (!f) return { ok: false, code: 'NOT_DECLARED' }
  const u = unitByCode(doseUnitCode)
  if (!u || (u.dimension !== 'mass_per_area' && u.dimension !== 'volume_per_area')) return { ok: false, code: 'UNIT_INVALID' }
  const wanted = f.basis === 'w/w' ? 'kg_ha' : 'l_ha'                                     // the basis' own quantity per hectare: kg for a mass basis, L for a volume one
  let perHa: string
  try { perHa = convert(doseValue, doseUnitCode, wanted, densityKgPerL === null || densityKgPerL === '' ? null : densityKgPerL) } catch (e) {
    if (e instanceof UnitError) return { ok: false, code: e.code }
    throw e
  }
  const q = parseDecimal(perHa)
  const out: Partial<Record<Nutrient, string>> = {}
  for (const n of NUTRIENTS) {
    const p = f[n]
    if (p === undefined) continue
    const r = parseDecimal(String(p))
    out[n] = roundedText({ n: q.n * r.n, d: q.d * r.d * 100n }, 3)
  }
  return { ok: true, kgPerHa: out }
}

/** The sum of several results per nutrient (a season's total on an area): exact on the three-decimal texts. */
export function sumNutrients(parts: ReadonlyArray<Partial<Record<Nutrient, string>>>): Partial<Record<Nutrient, string>> {
  const acc = new Map<Nutrient, bigint>()
  for (const p of parts) for (const n of NUTRIENTS) { const v = p[n]; if (v !== undefined) { const r = parseDecimal(v); acc.set(n, (acc.get(n) ?? 0n) + (r.n * 1000n) / r.d) } }
  const out: Partial<Record<Nutrient, string>> = {}
  for (const [n, v] of acc) out[n] = roundedText({ n: v, d: 1000n }, 3)
  return out
}
